import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { ContentLayout } from "@pages/content-layout";
import { WorkspaceProfileHeader } from "@pages/workspace-profile/workspace-profile-header";
import { WorkspaceInfo } from "@pages/workspace-profile/tab-contents/workspace-info-tab/workspace-info";
import { WorkspaceProjects } from "@pages/workspace-profile/tab-contents/workspace-projects-tab/workspace-projects";
import { LoadingScreen } from "@components/loading-screen";
import { WorkspaceGroups } from "@pages/workspace-profile/tab-contents/workspace-groups-tab/workspace-groups";
import { useAppParams } from "@router/router-helper";
import {
  devFeaturesStateSelector,
  setShouldShowHardwareLicense,
} from "@store/dev-features/dev-features-slice";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { CompanyProfileTab } from "@router/route-params";
import { PageNotFound } from "@pages/page-not-found";
import { OpenTabEvents } from "@utils/track-event/track-event-list";
import { useEffect } from "react";
import {
  fetchCompany,
  fetchCompanyMigrationStatus,
} from "@store/workspace/workspace-thunks";
import { companyStateSelector } from "@store/workspace/workspace-slice";
import { WorkspaceMembers } from "@pages/workspace-profile/tab-contents/workspace-members-tab/workspace-members";
import { PlansTable } from "@components/plans-table/plans-table";

/** Fetch company data and display the data in company profile page */
export function WorkspaceProfilePage(): React.JSX.Element {
  const { companyId, companyProfileTab } = useAppParams();
  const { shouldShowHardwareLicense } = useAppSelector(
    devFeaturesStateSelector
  );

  const { fetchingCompany, company } = useAppSelector(companyStateSelector);

  const dispatch = useAppDispatch();

  const { handleErrorWithPage } = useErrorHandler();

  // Fetches the company
  useEffect(() => {
    async function fetchCompanyData(): Promise<void> {
      try {
        await dispatch(fetchCompany({ companyId })).unwrap();
      } catch (error) {
        handleErrorWithPage({
          id: `CompanyProfilePage-${Date.now().toString()}`,
          title: "Could not load workspace",
          error,
        });
      }
    }

    void fetchCompanyData();
  }, [dispatch, companyId, handleErrorWithPage]);

  // Fetches whether the workspace has been migrated from HoloBuilder to Sphere XG, to show a badge
  // in the header. This is non-critical, so any failure just leaves the badge hidden.
  useEffect(() => {
    if (companyId) {
      void dispatch(fetchCompanyMigrationStatus({ companyId }));
    }
  }, [dispatch, companyId]);

  // Show hardware license tab if the current tab is hardware licenses added by the route param
  useEffect(() => {
    if (companyProfileTab === CompanyProfileTab.hardwareLicenses) {
      dispatch(setShouldShowHardwareLicense(true));
    }
  }, [companyProfileTab, dispatch]);

  if (
    !companyProfileTab ||
    !Object.values(CompanyProfileTab).includes(companyProfileTab)
  ) {
    return <PageNotFound />;
  }

  return (
    <LoadingScreen
      isFetching={fetchingCompany.selectedCompany === "pending"}
      data={company}
    >
      {company && (
        <ContentLayout
          header={<WorkspaceProfileHeader company={company} />}
          tabs={[
            {
              label: "Information",
              route: CompanyProfileTab.companyInformation,
              content: <WorkspaceInfo company={company} />,
            },

            {
              label: "Groups",
              route: CompanyProfileTab.groups,
              content: <WorkspaceGroups companyId={company.id} />,
            },

            {
              label: "Projects",
              route: CompanyProfileTab.projects,
              content: <WorkspaceProjects companyId={company.id} />,
            },

            {
              label: "Members",
              route: CompanyProfileTab.members,
              content: <WorkspaceMembers company={company} />,
            },

            {
              label: "Plans",
              route: CompanyProfileTab.plans,
              content: (
                <PlansTable subjectType="COMPANY" subjectId={company.id} />
              ),
            },
            {
              label: "Hardware Licenses",
              route: CompanyProfileTab.hardwareLicenses,
              content: (
                <PlansTable subjectType="DEVICE" subjectId={company.id} />
              ),
              isHidden: !shouldShowHardwareLicense,
            },
          ]}
          selectedTab={companyProfileTab}
          trackingEventPageName={OpenTabEvents.openCompanyProfileTabs}
        />
      )}
    </LoadingScreen>
  );
}
